<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  agents: { type: Array, default: () => [] },
  filters: { type: Object, required: true },
});

const emit = defineEmits(['apply', 'clear']);
const { t } = useI18n();
const draft = ref({ ...props.filters });

watch(
  () => props.filters,
  value => {
    draft.value = { ...value };
  },
  { deep: true }
);

const priorityOptions = [
  { value: '', label: t('POST_SALE.FILTERS.ALL_PRIORITIES') },
  { value: 'baixa', label: t('POST_SALE.PRIORITY.LOW') },
  { value: 'normal', label: t('POST_SALE.PRIORITY.NORMAL') },
  { value: 'alta', label: t('POST_SALE.PRIORITY.HIGH') },
  { value: 'critica', label: t('POST_SALE.PRIORITY.CRITICAL') },
];

const deadlineOptions = [
  { value: '', label: t('POST_SALE.FILTERS.ALL_DEADLINES') },
  { value: 'amarelos', label: t('POST_SALE.DEADLINES.ATTENTION') },
  { value: 'laranjas', label: t('POST_SALE.DEADLINES.DUE_SOON') },
  { value: 'vermelhos', label: t('POST_SALE.DEADLINES.OVERDUE') },
  { value: 'sem_prazo', label: t('POST_SALE.DEADLINES.NO_DEADLINE') },
];

const apply = () => emit('apply', { ...draft.value });
const clear = () => {
  draft.value = {
    q: '',
    responsible_user_id: '',
    priority: '',
    deadline: '',
  };
  emit('clear');
};
</script>

<template>
  <div
    class="grid gap-3 rounded-lais border border-lais-border-soft bg-white p-4 shadow-lais-card md:grid-cols-2 xl:grid-cols-4"
  >
    <label
      class="flex flex-col gap-1 text-sm font-semibold text-lais-text-body"
    >
      {{ t('POST_SALE.FILTERS.SEARCH') }}
      <input
        v-model="draft.q"
        type="search"
        class="min-h-10 rounded-lais border border-lais-border px-3 font-normal"
        @keyup.enter="apply"
      />
    </label>

    <label
      class="flex flex-col gap-1 text-sm font-semibold text-lais-text-body"
    >
      {{ t('POST_SALE.FILTERS.RESPONSIBLE') }}
      <select
        v-model="draft.responsible_user_id"
        class="min-h-10 rounded-lais border border-lais-border bg-white px-3 font-normal"
      >
        <option value="">{{ t('POST_SALE.FILTERS.ALL_RESPONSIBLES') }}</option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
    </label>

    <label
      class="flex flex-col gap-1 text-sm font-semibold text-lais-text-body"
    >
      {{ t('POST_SALE.FILTERS.PRIORITY') }}
      <select
        v-model="draft.priority"
        class="min-h-10 rounded-lais border border-lais-border bg-white px-3 font-normal"
      >
        <option
          v-for="option in priorityOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </label>

    <label
      class="flex flex-col gap-1 text-sm font-semibold text-lais-text-body"
    >
      {{ t('POST_SALE.FILTERS.DEADLINE') }}
      <select
        v-model="draft.deadline"
        class="min-h-10 rounded-lais border border-lais-border bg-white px-3 font-normal"
      >
        <option
          v-for="option in deadlineOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </label>

    <div class="flex justify-end gap-2 md:col-span-2 xl:col-span-4">
      <LaisPrimaryButton variant="ghost" @click="clear">
        {{ t('POST_SALE.ACTIONS.CLEAR') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton @click="apply">
        {{ t('POST_SALE.ACTIONS.APPLY') }}
      </LaisPrimaryButton>
    </div>
  </div>
</template>
